import React from 'react';

import {
  Autocomplete,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { HotelSearchForm } from '~/components/Common/Forms/HotelSearchForm';

import { useGetHotelOfferNames, useGetLeHotelOffers } from '~/hooks/apis/tours/useTourHotelAddOns';

import { TourOfferHotelAddOns } from '~/types/tours/tourOffer';

import NativeTourBedbankRoomSelect from './NativeTourBedbankRoomSelect';
import NativeTourCard from './NativeTourCard';
import NativeTourRemoveButton from './NativeTourRemoveButton';

interface Props {
  configurationId: string;
  value: TourOfferHotelAddOns;
  onChange: (value: TourOfferHotelAddOns) => void;
  startLocation: string | null;
  endLocation: string | null;
}

type Side = 'start' | 'end';

/** Hotels recommended for the nights before the tour starts and after it ends. */
export default function NativeTourOptionHotels({
  configurationId,
  value,
  onChange,
  startLocation,
  endLocation,
}: Props) {
  const ids = [...value.startLE, ...value.endLE, ...value.startBedbank, ...value.endBedbank];
  const { data: names = {} } = useGetHotelOfferNames(ids);
  const { data: leOffers } = useGetLeHotelOffers();

  const renderSide = (side: Side) => {
    const le = value[`${side}LE`];
    const bedbank = value[`${side}Bedbank`];
    const rooms = value[`${side}BedbankRoom`];
    const location = side === 'start' ? startLocation : endLocation;
    const set = (change: Partial<TourOfferHotelAddOns>) => onChange({ ...value, ...change });

    return (
      <NativeTourCard
        title={side === 'start' ? 'Before the tour' : 'After the tour'}
        description={location ? `Near ${location}` : 'Set the route to see where the tour starts and ends'}
      >
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" mb={1}>
              Flash and LPC hotels
            </Typography>
            {le.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None recommended.
              </Typography>
            ) : (
              <List dense disablePadding>
                {le.map((id, index) => (
                  <ListItem
                    key={id}
                    divider
                    secondaryAction={
                      <NativeTourRemoveButton
                        label="Remove hotel"
                        onClick={() => set({ [`${side}LE`]: le.filter((_, i) => i !== index) })}
                      />
                    }
                  >
                    <ListItemText primary={names[id] ?? id} secondary={id} />
                  </ListItem>
                ))}
              </List>
            )}
            <Box mt={1}>
              <HotelSearchForm
                id={`${configurationId}:${side}LE`}
                type="le"
                label="Add a hotel: search by location or property name"
                onSelect={(selection) =>
                  selection.id && !le.includes(selection.id) && set({ [`${side}LE`]: [...le, selection.id] })
                }
              />
              <Autocomplete
                size="small"
                sx={{ mt: 1 }}
                options={(leOffers ?? []).filter((offer) => !le.includes(offer.id))}
                getOptionLabel={(offer) => `${offer.id} · ${offer.type === 'hotel' ? 'Flash hotel' : 'LPC hotel'}`}
                value={null}
                onChange={(_event, offer) => offer && set({ [`${side}LE`]: [...le, offer.id] })}
                renderInput={(params) => <TextField {...params} label="Or pick by offer id" />}
              />
            </Box>
          </Box>
          <Box>
            <Typography variant="subtitle2" mb={1}>
              Bedbank hotels
            </Typography>
            {bedbank.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                None recommended.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {bedbank.map((id, index) => (
                  <Paper key={id} variant="outlined" sx={{ p: 1.5 }}>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
                      <ListItemText primary={names[id] ?? id} secondary={id} sx={{ flex: 1 }} />
                      <NativeTourBedbankRoomSelect
                        propertyId={id}
                        value={rooms[index] ?? null}
                        onChange={(room) =>
                          set({ [`${side}BedbankRoom`]: rooms.map((current, i) => (i === index ? room : current)) })
                        }
                      />
                      <NativeTourRemoveButton
                        label="Remove hotel"
                        onClick={() =>
                          set({
                            [`${side}Bedbank`]: bedbank.filter((_, i) => i !== index),
                            [`${side}BedbankRoom`]: rooms.filter((_, i) => i !== index),
                          })
                        }
                      />
                    </Stack>
                  </Paper>
                ))}
              </Stack>
            )}
            <Box mt={1}>
              <HotelSearchForm
                id={`${configurationId}:${side}Bedbank`}
                type="bedbank"
                label="Add a hotel: search by location, property name or id"
                onSelect={(selection) =>
                  selection.id &&
                  !bedbank.includes(selection.id) &&
                  set({ [`${side}Bedbank`]: [...bedbank, selection.id], [`${side}BedbankRoom`]: [...rooms, null] })
                }
              />
            </Box>
          </Box>
        </Stack>
      </NativeTourCard>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12, lg: 6 }}>{renderSide('start')}</Grid>
      <Grid size={{ xs: 12, lg: 6 }}>{renderSide('end')}</Grid>
    </Grid>
  );
}
